import { useQuery } from '@tanstack/react-query'
import { factsLocationTypes, factsSpecialtyGroups } from '@fireflyhealth/api'

import type { SpecialtyDropdownOption } from './types'

/**
 * Loads all dependencies for the Referral Details section of the Steerage View.
 * Caches indefinitely and performs the necessary mutations to get specialty groups
 * and location types into a single array.
 */
export const useReferralDetails = (): {
  isLoading: boolean
  specialtiesAndLocationTypes: SpecialtyDropdownOption[]
} => {
  const query = useQuery({
    queryKey: ['referralDetails'],
    queryFn: () =>
      Promise.all([
        factsLocationTypes.list().then(response => response.data),
        factsSpecialtyGroups.list().then(response => response.data),
      ]),
    staleTime: Infinity,
  })

  // Return default values if the query is loading or there's no data
  if (query.isPending || !query.data) {
    return {
      isLoading: true,
      specialtiesAndLocationTypes: [],
    }
  }

  const [locationTypes, specialtyGroups] = query.data

  // SpecialtyDropdownOption mirrors the generated snake_case shape, so each
  // response row only needs the `source` discriminator added.
  const specialtiesAndLocationTypes: SpecialtyDropdownOption[] = [
    ...specialtyGroups.map(group => ({ ...group, source: 'SpecialtyGroup' as const })),
    ...locationTypes.map(type => ({ ...type, source: 'LocationType' as const })),
  ].sort((a, b) => a.label.localeCompare(b.label))

  return { isLoading: false, specialtiesAndLocationTypes }
}
